import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useActiveWorkout } from '@/contexts/ActiveWorkoutContext';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { cn } from '@/lib/utils';
import {
  HomeIcon,
  DumbbellIcon,
  ListIcon,
  ChartIcon,
  ForkKnifeIcon,
} from '@/components/Icons';

const navItems = [
  { to: '/', icon: HomeIcon, label: 'Главная' },
  { to: '/workouts', icon: DumbbellIcon, label: 'Тренировки' },
  { to: '/exercises', icon: ListIcon, label: 'Упражнения' },
  { to: '/nutrition', icon: ForkKnifeIcon, label: 'Питание' },
  { to: '/analytics', icon: ChartIcon, label: 'Статистика' },
];

const WIDGET_POSITION_KEY = 'gymlab-active-workout-widget-position';

export default function Layout() {
  const { user } = useAuth();
  const { activeWorkout } = useActiveWorkout();
  const { isOnline, pendingMutations } = useNetworkStatus();
  const location = useLocation();
  const navigate = useNavigate();

  // Widget drag state
  const [widgetTop, setWidgetTop] = useState(() => {
    const saved = localStorage.getItem(WIDGET_POSITION_KEY);
    return saved ? parseFloat(saved) : 50; // Default: 50% (center)
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef<number>(0);
  const dragStartTop = useRef<number>(0);
  const hasMoved = useRef<boolean>(false);
  const isDraggingRef = useRef<boolean>(false);
  const widgetRef = useRef<HTMLButtonElement>(null);

  // Save position to localStorage
  useEffect(() => {
    localStorage.setItem(WIDGET_POSITION_KEY, widgetTop.toString());
  }, [widgetTop]);

  // Check if we're currently viewing the active workout or on home page
  const isOnActiveWorkout = activeWorkout && location.pathname === `/workouts/${activeWorkout.id}`;
  const isOnHomePage = location.pathname === '/';

  // Drag handlers
  const handleDragStart = (clientY: number) => {
    setIsDragging(true);
    isDraggingRef.current = true;
    hasMoved.current = false;
    dragStartY.current = clientY;
    dragStartTop.current = widgetTop;
  };

  const handleDragMove = (clientY: number) => {
    if (!isDraggingRef.current) return;

    const deltaY = clientY - dragStartY.current;
    
    // Mark as moved if moved more than 5px
    if (Math.abs(deltaY) > 5) {
      hasMoved.current = true;
    }

    const widgetHeight = 80; // Height of widget in pixels
    const viewportHeight = window.innerHeight;
    
    // Calculate new position in percentage
    const deltaPercent = (deltaY / viewportHeight) * 100;
    let newTop = dragStartTop.current + deltaPercent;
    
    // Constrain to viewport bounds (accounting for widget height)
    const minTop = (widgetHeight / 2 / viewportHeight) * 100;
    const maxTop = 100 - (widgetHeight / 2 / viewportHeight) * 100;
    newTop = Math.max(minTop, Math.min(maxTop, newTop));
    
    setWidgetTop(newTop);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    isDraggingRef.current = false;
    // Reset after a short delay to allow onClick to check hasMoved
    setTimeout(() => {
      hasMoved.current = false;
    }, 100);
  };

  // Mouse events
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleDragStart(e.clientY);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      handleDragMove(e.clientY);
    };

    const handleMouseUp = () => {
      handleDragEnd();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, widgetTop]);

  // Touch events
  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const touch = e.touches[0];
    if (touch) handleDragStart(touch.clientY);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.touches[0];
      if (touch) handleDragMove(touch.clientY);
    };

    const handleTouchEnd = () => {
      handleDragEnd();
    };

    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, widgetTop]);

  return (
    <div className="min-h-full bg-gray-50 overflow-x-hidden">
      {/* Offline indicator */}
      {(!isOnline || pendingMutations > 0) && (
        <div className={`fixed top-0 left-0 right-0 z-[9999] ${isOnline ? 'bg-amber-500' : 'bg-gray-700'} text-white text-center py-1 text-sm font-medium`}>
          {!isOnline ? (
            <div className="flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
              </svg>
              Нет подключения
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2">
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Синхронизация ({pendingMutations})
            </div>
          )}
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col md:fixed md:inset-y-0 md:w-64 bg-white border-r border-gray-200">
        <div className="flex items-center h-16 px-6 border-b border-gray-200">
          <DumbbellIcon className="w-8 h-8 text-primary-600" />
          <span className="ml-2 text-xl font-bold text-gray-900">GymLab</span>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors',
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                )
              }
            >
              <item.icon className="w-5 h-5 mr-3" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-200">
          <Link
            to="/profile"
            className="flex items-center p-2 -m-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden">
              {user?.avatar_base64 ? (
                <img
                  src={user.avatar_base64}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-sm font-medium text-primary-700">
                  {(user?.display_name || user?.login || '?').charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">{user?.display_name || user?.login}</p>
              <p className="text-xs text-gray-500">Открыть профиль</p>
            </div>
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </Link>
        </div>
      </aside>

      {/* Main content */}
      <main className="md:ml-64">
        <Outlet />
      </main>

      {/* Active Workout Floating Widget - show when not on workout page and not on home page */}
      {activeWorkout && !isOnActiveWorkout && !isOnHomePage && (
        <button
          ref={widgetRef}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onClick={() => {
            // Only navigate if not dragging and didn't move
            if (!isDragging && !hasMoved.current) {
              navigate(`/workouts/${activeWorkout.id}`);
            }
          }}
          className="md:hidden fixed left-0 z-40 bg-primary-600 text-white rounded-r-2xl shadow-lg flex items-center justify-center active:bg-primary-700 transition-colors touch-none select-none"
          style={{ 
            width: '36px', 
            height: '80px',
            top: `${widgetTop}%`,
            transform: 'translateY(-50%)',
            cursor: isDragging ? 'grabbing' : 'grab',
          }}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
        </button>
      )}

      {/* Mobile bottom navigation */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 z-50">
        <div className="flex justify-around">
          {navItems.map((item) => {
            const isActive =
              item.to === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.to);

            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={cn('nav-link flex-1', isActive && 'active')}
              >
                <item.icon className="w-6 h-6" />
                <span className="mt-1">{item.label}</span>
              </NavLink>
            );
          })}
        </div>
        {/* Safe area spacer for iOS */}
        <div className="safe-bottom" />
      </nav>
    </div>
  );
}
